<template>
  <div
    :id="id"
    v-click-outside="handleClickOutside"
    class="vcl-select"
    :style="containerStyles"
  >
    <div @click.stop="handleClick">
      <vp-input
        v-if="mode !== 'tag'"
        ref="dropdown__selected"
        class="vcl-select__selected"
        :value="selectedValueContent"
        :disabled="disabled"
        :placeholder="computedEmptyOption"
        :readonly="!searchOption"
        :styles="selectStyles"
        :height="selectHeight"
        :width="selectWidth"
        :aria-label="ariaLabel"
        role="combobox"
        @keydown="handleKeydown"
        @input="handleInput"
        @click.stop="handleInputClick"
      >
        <template v-if="$slots.label" #label>
          <slot name="label" />
        </template>
        <template v-if="$slots.affix" #affix>
          <div class="vcl-select__affix">
            <slot name="affix" />
          </div>
        </template>
        <template #suffix>
          <slot name="arrow">
            <vp-icon
              class="vcl-select__arrow"
              :class="{ 'open': isOpen, 'is-disabled': disabled }"
              icon="down_arrow"
              :style="iconStyles"
              @click.stop="handleInputClick"
            />
          </slot>
        </template>
      </vp-input>

      <div v-if="$slots.label && mode === 'tag'" class="vcl-select-tag__label-row">
        <label ref="label" class="vcl-select-tag__label" :for="_uid+'VpSelectTag'">
          <slot name="label" />
        </label>
      </div>
      <div
        v-if="mode === 'tag'"
        :id="_uid+'VpSelectTag'"
        ref="tagContainer"
        class="vcl-select__tag-container"
        role="combobox"
        tabindex="0"
        :class="[{ 'has-focus' : tagContainerHasFocus }]"
        :style="tagContainerStyles"
        @click="handleTagContainerClick"
        @focus="handleTagContainerFocus"
        @keydown="handleTagContainerKeydown"
        @blur="handleTagContainerBlur"
        v-html="selectedTagsContent"
      ></div>

      <div
        v-show="isOpen && !disabled"
        ref="dropdown"
        role="listbox"
        class="vcl-select__menu"
        :style="menuStyles"
      >
        <div
          v-for="(option, i) of list"
          v-show="(mode === 'multi') || ((mode === 'single') && !searchOption) || ((mode === 'single') && searchOption && filterMatchState[i]) || (mode === 'tag' && !listSelectionState[i])"
          :key="i"
          :class="['vcl-select__menu-item', { 'is-disabled': option.disabled }]"
          tabindex="-1"
          role="option"
          @focus="handleOptionFocus"
          @click.stop.prevent="handleOptionClick(i)"
          @mousemove="handleMouseMove(i)"
          @keydown="handleKeydown"
        >
          <slot name="option" :option="option" :isSelected="option.value === (options[selectedIndex] || {}).value" :isFocused="i === focusIndex">
            <vp-checkbox v-if="mode === 'multi'" :value="listSelectionState[i]" :styles="checkboxStylesProp" />
            <p class="vcl-select__menu-item-name" :style="{ paddingLeft: mode === 'multi' ? '8px' : '0' }">{{ option.name }}</p>
          </slot>
        </div>
        <div v-if="(searchOption && options && !filteredResultCount) || ((mode === 'tag') && (value !== null) && (options.length === value.length))">
          <slot name="no-results">
            <div class="vcl-select__no-results">{{ emptyResult }}</div>
          </slot>
        </div>
      </div>
    </div>
    <span v-if="$slots.error" class="vcl-select__error-message">
      <slot name="error" />
    </span>
  </div>
</template>

<script>
import VpIcon from '../vp-icon/VpIcon.vue';
import VpInput from '../vp-input/VpInput.vue';
import { clickOutside } from '../../utilities/directives';

export default {
  name: 'VpSelect',
  components: {
    VpIcon,
    VpInput,
  },
  directives: {
    clickOutside,
  },
  props: {
    id: {
      type: String,
      default: '',
    },
    options: {
      type: Array,
      required: true,
      default() {
        return [];
      },
    },
    value: {
      type: [String, Number, Array, Boolean],
      required: false,
      default: null,
    },
    emptyOption: {
      type: String,
      required: false,
      default: 'Select One',
    },
    emptyResult: {
      type: String,
      required: false,
      default: 'No result found.',
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    searchOption: {
      type: Boolean,
      required: false,
      default: false,
    },
    mode: {
      type: String,
      required: false,
      default: 'single',
    },
    width: {
      type: String,
      default: null,
    },
    height: {
      type: String,
      default: null,
    },
    styles: {
      type: Object,
      default() {
        return {};
      },
    },
    ariaLabel: {
      type: String,
      default: null,
    }
  },
  emits: ['input'],
  data() {
    return {
      list: [],
      isOpen: false,
      selected: null, // the selected value (string) or array in multi or tag mode
      selectedIndex: -1,
      listSelectionState: [], // for multi and tag mode, boolean true/false for each option to indicate if they are selected
      filterMatchState: [], // like listSelectionState, but for searchable selects
      focusIndex: null,
      matchedIndexes: [],
      currentTypedWord: '', // the recorded keystrokes used for jumping to options in the options list
      lastKeyboardEvent: null, // unix timestamp of last keyboard event, used to reset currentTypeWord after 1s
      searchOptionFilterText: '',
      filteredResultCount: 0,
      tagContainerHasFocus: false,
      closeIcon: '<svg class="closeIconSvg" width="8" height="8" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" style="line-height:30px; margin:0;"><path class="closeIconPath" d="M13.65 12.075L12.075 13.65C11.725 13.825 11.55 14 11.2 14C10.85 14 10.675 13.825 10.325 13.65L6.825 10.15L3.325 13.65C3.325 13.825 2.975 14 2.8 14C2.45 14 2.1 13.825 1.925 13.65L0.35 12.075C0.175 11.725 0 11.55 0 11.2C0 10.85 0.175 10.675 0.35 10.325L3.85 6.825L0.35 3.325C0.175 3.325 0 2.975 0 2.8C0 2.45 0.175 2.275 0.35 1.925L1.925 0.35C2.1 0.175 2.45 0 2.8 0C2.975 0 3.325 0.175 3.5 0.35L7 3.85L10.5 0.35C10.675 0.175 10.85 0 11.2 0C11.55 0 11.725 0.175 12.075 0.35L13.65 1.925C13.825 2.1 14 2.45 14 2.8C14 2.975 13.825 3.325 13.65 3.5L10.15 7L13.65 10.5C13.825 10.675 14 10.85 14 11.2C14 11.55 13.825 11.725 13.65 12.075Z" fill="#1B3664" /></svg>'
    };
  },
  computed: {
    selectStyles() {
      const defaultStyles = {
        inputSuffixPadding: '0',
        height: this.styles.height || 'auto',
        userSelect: this.searchOption ? 'auto' : 'none',
        cursor: this.disabled ? 'not-allowed' : 'default',
        selectionBackgroundcolor: this.searchOption ? null : 'transparent',
      };

      if (this.searchOption) {
        defaultStyles.userSelect = 'auto';
        defaultStyles.selectionBackgroundcolor = null;
        defaultStyles.cursor = this.disabled ? 'not-allowed' : 'text';
      }

      const select = this.styles.select || {};
      const styles = {
        'backgroundColor': this.styles.backgroundColor || '#FFFFFF',
        'border': this.styles.border || '1px solid #AFAFAF',
        '--select-border-radius': this.styles.borderRadius || '6px',
        '--select-height': select.height || 'auto',
        'fontSize': this.styles.fontSize || '16px',
      };

      const retVal = { ...defaultStyles, ...styles };
      return retVal;
    },
    selectHeight() {
      let height = '45px';
      if (this.styles.height) {
        height = this.styles.height;
      }
      if (this.height) {
        height = this.height;
      }
      return height;
    },
    selectWidth() {
      let width = '350px';
      if (this.styles.width) {
        width = this.styles.width;
      }
      if (this.width) {
        width = this.width;
      }
      return width;
    },
    containerStyles() {
      const styles = {
        '--select-font-size': this.styles.fontSize || '16px',
      };
      let width = '350px';
      if (this.styles.width) {
        width = this.styles.width;
      }
      if (this.width) {
        width = this.width;
      }
      styles.width = width;
      return styles;
    },
    menuStyles() {
      const menu = this.styles.menu || {};
      const styles = {
        '--select-menu-width': menu.width || 'auto',
        '--select-menu-min-width': menu.minWidth || '100%',
        '--select-menu-max-width': menu.maxWidth || 'auto',
        '--select-menu-max-height': menu.maxHeight || '250px',
        '--select-menu-border': menu.border || '1px solid #AFAFAF',
        '--select-menu-border-radius': menu.borderRadius || '6px',
        '--select-menu-box-shadow': menu.boxShadow || '0px 2px 4px rgba(0, 0, 0, 0.1)',
        '--select-menu-item-height': menu.itemHeight || '45px',
      };
      let width = '350px';
      if (this.styles.width) {
        width = this.styles.width;
      }
      if (this.width) {
        width = this.width;
      }
      if (menu.width) {
        width = menu.width;
      }
      styles.width = width;
      return styles;
    },
    checkboxStylesProp() {
      return {
        size: 16
      };
    },
    iconStyles() {
      const arrow = this.styles.icon || {};
      const arrowStyle = {
        '--select-arrow-font-size': arrow.fontSize || '12px',
        '--select-arrow-right': arrow.rightPadding || '1em',
        '--select-arrow-disabled-color': arrow.disabledColor || '#AFAFAF',
      };
      return arrowStyle;
    },
    tagContainerStyles() {
      const style = {
        width: '350px'
      };
      if (this.styles.select && this.styles.select.width) {
        style.width = this.styles.select.width;
      }
      return style;
    },
    selectedValueContent() {
      if (this.mode === 'single') {
        if (this.searchOption) {
          return this.searchOptionFilterText;
        }
        if (this.selected === null) {
          return null;
        }
        return this.selected.name;
      } if (this.mode === 'multi') {
        if (this.selected === null) {
          return '';
        }
        if (this.selected.length === 0) {
          return null;
        }
        return `${this.selected.length} Selected`;
      }
      return '';
    },
    selectedTagsContent() {
      if (this.selected === null) { return ''; }
      let markup = '';
      if ((this.selected !== '') && (typeof this.selected === 'object')) {
        this.selected.forEach((itemValue) => {
          const index = this.options.findIndex((opt) => opt.value === itemValue);
          const itemName = this.options[index].name;
          markup += `<div class="vcl-select__tag">${itemName}`
            + `<div class="tagDeleteDiv" value="${itemValue}">${this.closeIcon}</div></div>`;
        });
        return markup;
      }
      return '';
    },
    computedEmptyOption() {
      if (this.searchOption && (this.searchOptionFilterText !== '')) {
        return this.searchOptionFilterText;
      }
      return this.emptyOption;
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        this.init(value, this.list);
      },
    },
    options: {
      immediate: true,
      handler(newOptions) {
        this.list = newOptions;
        if (newOptions !== null) {
          this.filteredResultCount = newOptions.length;
        } else {
          this.filteredResultCount = 0;
        }
        this.init(this.value, this.list);
      },
    },
    selected: {
      handler() {
        if (this.$refs.tagContainer !== undefined) {
          if (this.$refs.tagContainer.children.length > 0) {
            const children = [].slice.call(this.$refs.tagContainer.children);
            children.forEach((child) => {
              child.addEventListener('click', (event) => {
                event.preventDefault();
              });
            });
          }
        }
      }
    },
  },
  created() {
  },
  mounted() {
  },
  methods: {
    init(value, options) {
      if ((this.mode === 'multi') || (this.mode === 'tag')) {
        if (value !== null) {
          this.selected = value;
          this.options.forEach((option, i) => {
            if (value.indexOf(option.value) !== -1) {
              this.listSelectionState[i] = true;
            } else {
              this.listSelectionState[i] = false;
            }
          });
        }
      } else {
        this.selectedIndex = this.findSelectedIndex(value, options);
        if (this.selectedIndex !== null) {
          this.selected = options[this.selectedIndex] || null;
        }
      }
      if (this.searchOption) {
        this.searchOptions(value);
      }
    },
    handleClick(evt = MouseEvent) {
      const allowListedEvents = [
        /** if radio injection, let event propagate */
        evt.target.type === 'radio',
        evt.target.className === 'radio-option',
        // click on row's white space should do nothing
        evt.target.className === 'vcl-input__label-row'
      ]
      if (allowListedEvents.some(event => Boolean(event))) return;

      // clicking on the select label should focus on the select
      if (evt.target.className === 'vcl-input__label') {
        this.$refs.dropdown__selected.focus();
      } else if (evt.target.className === 'vcl-select-tag__label') {
        this.$refs.tagContainer.focus();
      } else {
        if (this.mode === 'tag') { return; }
        if (this.isOpen) {
          this.close();
        } else {
          this.open();
        }
      }
      return evt.preventDefault();
    },
    handleInputClick() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    },
    close() {
      this.isOpen = false;
      if (this.searchOption && (this.selected === '' || !this.selected)) {
        this.list = [...this.options];
      }
      if (this.mode !== 'tag') {
        this.$refs.dropdown__selected.focus();
      }
    },
    open() {
      this.isOpen = true;

      this.$nextTick(() => {
        if (this.searchOption) {
          this.focusOnItem(null);
        } else if (this.selectedIndex !== -1) {
          this.focusOnItem(this.selectedIndex || 0);
        }
      });
    },
    deselectOption(index) {
      this.listSelectionState[index] = false;
      const option = this.options[index];
      const selIndex = this.selected.findIndex((el) => option.value === el);
      this.selected.splice(selIndex, 1);
      this.$emit('input', this.selected);
    },
    selectOption(index) {
      const option = this.options[index];
      if (option.disabled) return;
      
      if ((this.mode === 'multi') || (this.mode === 'tag')) {
        if ((this.mode === 'multi') && (this.listSelectionState[index])) {
          this.deselectOption(index);
          return;
        }
        this.listSelectionState[index] = true;

        if (this.selected === null) {
          this.selected = [option.value];
        } else if (this.selected.length === 0) {
          this.selected.push(option.value);
        } else {
          const selIndex = this.selected.findIndex((el) => option.value.toString() === el.toString());
          if (selIndex === -1) {
            this.selected.push(option.value);
          }
        }

        // in tag mode selected options are removed from the list of available options
        // we have to update the focusedIndex to the next element
        if (this.mode === 'tag') {
          const nextIndex = this.options.findIndex((opt, idx) => (opt.selected === false && (idx > this.focusIndex)));
          if (nextIndex !== -1) {
            this.focusOnItem(nextIndex);
          } else {
            this.first();
          }
        }
        this.$emit('input', this.selected);
      } else {
        this.selected = option;
        this.searchOptionFilterText = option.name;

        this.selectedIndex = this.findSelectedIndex(option.value, [...this.options]);
        if (option.value !== null) {
          this.$emit('input', option.value);
        }
      }
    },
    findFirstMatch(startIndex) {
      let firstMatch = -1;
      for (let i = startIndex; i < this.options.length; i += 1) {
        if ((this.searchOption) && this.filterMatchState[i]) {
          firstMatch = i;
          break;
        } else if ((this.mode === 'tag') && !this.listSelectionState[i]) {
          firstMatch = i;
          break;
        }
      }
      return firstMatch;
    },
    findLastMatch(startIndex) {
      let lastMatch = -1;
      for (let i = startIndex; i >= 0; i -= 1) {
        if (this.searchOption && this.filterMatchState[i]) {
          lastMatch = i;
          break;
        } else if ((this.mode === 'tag') && !this.listSelectionState[i]) {
          lastMatch = i;
          break;
        }
      }
      return lastMatch;
    },
    previous() {
      if (!this.searchOption && (this.mode !== 'tag')) {
        if ((this.focusIndex === null) || (this.focusIndex === 0)) {
          if (this.isOpen) {
            // uncomment this if we want to allow rolling over from the first option to the last
            // this.focusOnItem(this.options.length - 1);
          }
        } else {
          this.focusOnItem(this.focusIndex - 1);
        }
      } else if (this.searchOption) {
        if (this.focusIndex === null) {
          const lastMatchId = this.findLastMatch((this.options.length - 1));
          if (lastMatchId !== -1) {
            this.focusOnItem(lastMatchId);
          }
        } else {
          const previousMatch = this.findLastMatch(this.focusIndex - 1);
          if (previousMatch !== -1) {
            this.focusOnItem(previousMatch);
          } else {
            this.focusIndex = null;
            this.$refs.dropdown__selected.focus();
          }
        }
      } else if (this.mode === 'tag') {
        if (this.focusIndex === null) {
          // find last unselected option
          const lastOptionNotSelected = this.findLastMatch(this.options.length - 1);
          if (lastOptionNotSelected !== -1) {
            this.focusOnItem(lastOptionNotSelected);
          }
        } else {
          let previousOptionNotSelected = this.findLastMatch(this.options.length - 1);
          for (let i = (this.focusIndex - 1); i >= 0; i -= 1) {
            if (!this.listSelectionState[i]) {
              previousOptionNotSelected = i;
              break;
            }
          }
          if (previousOptionNotSelected !== -1) {
            this.focusOnItem(previousOptionNotSelected);
          }
        }
      }
    },
    next() {
      if (!this.searchOption && (this.mode !== 'tag')) {
        if (this.focusIndex === null) {
          this.focusOnItem(0);
        } else if ((this.focusIndex + 1) === this.options.length) {
          // uncomment to support rolling over from last option to first
          // this.focusOnItem(0);
        } else {
          this.focusOnItem(this.focusIndex + 1);
        }
      } else if (this.searchOption) {
        if (this.filteredResultCount > 0) {
          if (this.focusIndex === null) {
            const firstIndex = this.filterMatchState.findIndex((match) => match === true);
            this.focusOnItem(firstIndex);
          } else {
            const nextIndex = this.filterMatchState.findIndex((match, index) => (match && (index > this.focusIndex)));
            if (nextIndex !== -1) {
              this.focusOnItem(nextIndex);
            } else {
              this.focusIndex = null;
              this.$refs.dropdown__selected.focus();
            }
          }
        }
      } else if (this.mode === 'tag') {
        let nextIndex = -1;
        for (let i = this.focusIndex + 1; i < this.options.length; i += 1) {
          if (!this.listSelectionState[i]) {
            nextIndex = i;
            break;
          }
        }
        if (this.focusIndex === null || nextIndex === -1) {
          this.first();
        } else {
          this.focusOnItem(nextIndex);
        }
      }
    },
    first() {
      if (this.searchOption || (this.mode === 'tag')) {
        const firstMatch = this.findFirstMatch(0);
        if (firstMatch !== -1) {
          this.focusOnItem(firstMatch);
        }
      } else {
        this.focusOnItem(0);
      }
    },
    last() {
      if (this.searchOption || (this.mode === 'tag')) {
        const lastMatch = this.findLastMatch((this.options.length - 1));
        if (lastMatch !== -1) {
          this.focusOnItem(lastMatch);
        }
      } else {
        this.focusOnItem(this.options.length - 1);
      }
    },
    pageUp() {
      // TODO: This should be calculated by the height of the menu and each option
      const itemsPerPage = 4;
      if (this.searchOption) {
        let matchCount = 0;
        let prevMatch = -1;
        for (let i = this.focusIndex; i >= 0; i -= 1) {
          if (this.filterMatchState[i]) {
            matchCount += 1;
            prevMatch = i;
          }
          if (matchCount === itemsPerPage) { break; }
        }
        if (prevMatch !== -1) {
          this.focusOnItem(prevMatch);
        }
      } else if (this.mode === 'tag') {
        let matchCount = 0;
        let prevMatch = -1;
        for (let i = this.focusIndex; i >= 0; i -= 1) {
          if (this.listSelectionState[i]) {
            matchCount += 1;
            prevMatch = i;
          }
          if (matchCount === itemsPerPage) { break; }
        }
        if (prevMatch !== -1) {
          this.focusOnItem(prevMatch);
        }
      } else if (this.focusIndex < 4) {
        this.focusOnItem(0);
      } else {
        this.focusOnItem(this.focusIndex - itemsPerPage);
      }
    },
    pageDown() {
      // TODO: This should be calculated by the height of the menu and each option
      const itemsPerPage = 4;
      if (this.searchOption) {
        let matchCount = 0;
        let nextMatch = -1;
        for (let i = this.focusIndex; i < this.options.length; i += 1) {
          if (this.filterMatchState[i]) {
            matchCount += 1;
            nextMatch = i;
          }
          if (matchCount === itemsPerPage) { break; }
        }
        if (nextMatch !== -1) {
          this.focusOnItem(nextMatch);
        }
      } else if (this.mode === 'tag') {
        let matchCount = 0;
        let nextMatch = -1;
        for (let i = this.focusIndex; i < this.options.length; i += 1) {
          if (!this.listSelectionState[i]) {
            matchCount += 1;
            nextMatch = i;
          }
          if (matchCount === itemsPerPage) { break; }
        }
        if (nextMatch !== -1) {
          this.focusOnItem(nextMatch);
        }
      } else if (this.focusIndex > (this.options.length - itemsPerPage)) {
        this.focusOnItem(this.options.length - 1);
      } else {
        this.focusOnItem(this.focusIndex + itemsPerPage);
      }
    },
    focusOnItem(index) {
      this.focusIndex = index;

      if ((this.mode === 'single') && !this.isOpen) {
        if (index !== null) {
          this.selectOption(index);
        }
        return;
      }

      if (this.mode !== 'tag') {
        const element = index === null ? this.$refs.dropdown__selected : this.$refs.dropdown.children[index];
        if (element) {
          element.focus();
        }
      }
      if (this.$refs.dropdown.children[index] !== undefined) {
        this.$refs.dropdown.children[index].focus();
      }
    },
    focusOnNextOption(key) {
      // find the options that match the first letter
      const matches = this.options.filter((option) => {
        if (option.name.toLowerCase().charAt(0) === key) {
          return true;
        } return false;
      });

      // see if the currently focused item is in the list of matches
      const indexWithinMatches = matches.findIndex((option) => {
        if (this.options[this.focusIndex].name === option.name) {
          return true;
        } return false;
      });

      // we want to set the focus to the first element in the matches if we aren't currently
      // selecting one, or to the next matching element if we are
      let nextIndex = 0;
      if (indexWithinMatches !== -1) {
        nextIndex = indexWithinMatches + 1;
      }

      // if we hit the end of the matches list go back to the beginning
      if (nextIndex >= matches.length) { nextIndex = 0; }

      // get the next value, and find its index in the full options list, focus on it
      const nextMatch = matches[nextIndex];

      // there may not be a match for what the user typed in
      // check nextMatch to make sure it is defined
      if (nextMatch !== undefined) {
        const nextIndexForFocus = this.options.findIndex((option) => {
          if (option.value === nextMatch.value) { return true; } return false;
        });
        if (nextIndexForFocus !== -1) {
          this.focusOnItem(nextIndexForFocus);
        }
      }
    },
    searchOptions(value) {
      const filterText = value && String(value || '').toLowerCase();
      let newLength = 0;

      this.list.forEach((listItem, i) => {
        if (listItem.name.toLowerCase().indexOf(filterText) === -1) {
          this.filterMatchState[i] = false;
        } else {
          this.filterMatchState[i] = true;
          newLength += 1;
        }
      });
      this.filteredResultCount = newLength;
    },
    valueReset() {
      if ((this.mode === 'single') && (this.searchOption)) {
        this.selected = (this.options[this.selectedIndex] || { name: '' }).name;
      }
    },
    findSelectedIndex(value, options) {
      if ((value === undefined) || (value === null)) return null;
      const index = options.findIndex((option) => option.value.toString() === value.toString());
      return index === -1 ? null : index;
    },
    optionSelected(optionToCheck) {
      if (this.mode === 'multi') {
        const index = this.selected.findIndex((option) => option.value.toString() === optionToCheck.value.toString());
        if (index === -1) {
          return false;
        } return true;
      }
      return false;
    },
    findMatchedOptions() {
      let matchId = -1;
      for (let i = 0; i < this.options.length; i += 1) {
        if (this.currentTypedWord
            === this.options[i].name.toLowerCase().substring(0, this.currentTypedWord.length)) {
          matchId = i;
          break;
        }
      }
      return matchId;
    },
    handleTagContainerClick(event) {
      this.tagContainerHasFocus = true;
      if (event.target.classList[0] === 'closeIconPath') {
        this.handleTagDelete(event.target.parentNode.parentNode.getAttribute('value'));
      } else if (event.target.classList[0] === 'closeIconSvg') {
        this.handleTagDelete(event.target.parentNode.getAttribute('value'));
      } else if (event.target.classList[0] === 'tagDeleteDiv') {
        this.handleTagDelete(event.target.getAttribute('value'));
      } else if (event.target.classList[0] === 'vcl-select__tag-container') {
        if (!this.isOpen) {
          this.open();
        } else {
          this.close();
        }
      }
    },
    handleTagContainerFocus() {
      this.tagContainerHasFocus = true;
    },
    handleTagContainerBlur() {
      this.tagContainerHasFocus = false;
      setTimeout(() => {
        if (!this.tagContainerHasFocus) { this.close(); }
      }, 250);
    },
    handleOptionClick(i) {
      this.selectOption(i);
      if (this.mode === 'single') { this.close(); }
    },
    handleOptionFocus() {
      this.tagContainerHasFocus = true;
    },
    handleTagDelete(tag) {
      const index = this.options.findIndex((option) => option.value.toString() === tag);
      if (index !== -1) {
        this.listSelectionState[index] = false;
        const selIndex = this.selected.findIndex((option) => option.toString() === tag);
        this.selected.splice(selIndex, 1);
        this.$emit('input', this.selected);
      }
    },
    handleInput(txt) {
      // the @input handler should only be hit on selects with searchOption=true, but just in case
      if (this.searchOption) {
        this.searchOptionFilterText = txt;
        this.isOpen = true;

        if (this.searchOption) {
          this.searchOptions(txt, this.options);
        }
      }
    },
    handleInputDelete() {
      if (this.searchOption) {
        if (this.value !== '') {
          this.searchOptionFilterText = '';
          this.$emit('input', '');
          this.isOpen = false;
        } else {
          this.searchOptionFilterText = this.searchOptionFilterText.slice(0, -1);
          this.searchOptions(this.searchOptionFilterText, this.options);
          this.focusOnItem(0);
          this.$refs.dropdown__selected.focus();
        }
      }
    },
    handleClickOutside() {
      this.isOpen = false;
      this.valueReset();
      if (this.mode === 'tag') {
        this.tagContainerHasFocus = false;
      }
    },
    handleKeydown(evt) {
      if ((evt.key === 'ArrowDown') || (evt.key === 'ArrowRight')) {
        this.next();
        evt.preventDefault();
      } else if ((evt.key === 'ArrowUp') || (evt.key === 'ArrowLeft')) {
        this.previous();
        evt.preventDefault();
      } else if ((evt.key === 'ArrowLeft') || (evt.key === 'ArrowRight')) {
        evt.preventDefault();
      } else if (evt.key === 'Home') {
        this.first();
        evt.preventDefault(); // stop outer page scroll
      } else if (evt.key === 'End') {
        this.last();
        evt.preventDefault(); // stop outer page scroll
      } else if (evt.key === 'PageUp') {
        this.pageUp();
        evt.preventDefault();
      } else if (evt.key === 'PageDown') {
        this.pageDown();
        evt.preventDefault();
      } else if (evt.key === 'Escape') {
        this.close();
        this.valueReset();
      } else if ((evt.key === 'Space') || (evt.key === ' ')) {
        if (!this.isOpen) {
          this.open();
        } else if ((this.focusIndex !== null) && (this.mode === 'multi')) {
          this.selectOption(this.focusIndex);
        }

        if (!this.searchOption) {
          evt.stopPropagation();
          evt.preventDefault();
        }

        const now = Date.now();
        if (now - this.lastKeyboardEvent < 1000) {
          this.handleAlphaNumericKeypress(evt);
        }
      } else if (evt.key === 'Tab') {
        if ((this.focusIndex !== null) && (this.mode === 'single')) {
          this.selectOption(this.focusIndex);
        }
        this.close();
      } else if (evt.key === 'Backspace') {
        this.handleInputDelete();
        evt.preventDefault();
      } else if (evt.key === 'Enter') {
        if (this.focusIndex !== null) {
          this.selectOption(this.focusIndex);
          if (this.mode === 'single') {
            this.close();
          }
        }
      } else if (!this.searchOption && (evt.keyCode >= 48 && evt.keyCode <= 90)) {
        this.handleAlphaNumericKeypress(evt);
      }
    },
    handleAlphaNumericKeypress(evt) {
      let allSameLetter = true;
      const now = Date.now();

      for (let i = 0; i < this.currentTypedWord.length; i += 1) {
        if (this.currentTypedWord.charAt(i) !== evt.key) {
          allSameLetter = false;
          break;
        }
      }
      if (allSameLetter) {
        this.focusOnNextOption(evt.key);
        this.currentTypedWord += evt.key;
      } else {
        if (now - this.lastKeyboardEvent > 1000) {
          this.currentTypedWord = evt.key;
        } else {
          this.currentTypedWord += evt.key;
        }
        const optionId = this.findMatchedOptions();
        if (optionId !== -1) {
          this.focusOnItem(optionId);
        }
      }
      this.lastKeyboardEvent = now;
    },
    handleTagContainerKeydown(evt) {
      if ((evt.key === 'Space') || (evt.key === ' ')) {
        if (!this.isOpen) {
          this.open();
        }
        evt.stopPropagation();
        evt.preventDefault();
      } else if (evt.key === 'ArrowDown') {
        this.first();
        evt.preventDefault();
      }
    },
    handleMouseMove(index) {
      this.focusOnItem(index);
    },
  },
};
</script>

<style lang="scss" scoped>
.vcl-select {
  $self: &;
  position: relative;
  text-align: left;
  outline: none;
  box-sizing: border-box;
  &__selected div.vcl-input__input input {
    margin-top:5px !important;
  }
  &__selected, &__tag-container {
    background-color: #FFFFFF;
    background-color: var(--select-background-color);
    border-radius: 6px;
    border-radius: var(--select-border-radius);
    border: 1px solid #AFAFAF;
    border: var(--select-border);
    font-size: 16px;
    font-size: var(--select-font-size);
    cursor: default;
    user-select: none;
    box-sizing: border-box;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #4F4F4F;
    &.is-disabled {
      color: #AFAFAF;
      color: var(--select-disabled-color);
      background-color: #fff;
      background-color: var(--select-disabled-background-color);
      cursor: not-allowed;
    }
    #{ $self }__arrow {
      cursor: default;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      color: #4F4F4F;
      font-size: 12px;
      font-size: var(--select-arrow-font-size);
      right: 1em;
      right: var(--select-arrow-right);
      &.open {
        transform: translateY(-50%) rotate(180deg);
      }
      &.is-disabled {
        transform: rotate(0deg);
        margin-top: -5px;
        color:#afafaf;
        color: var(--select-arrow-disabled-color);
        cursor: not-allowed;
      }
    }
  }
  &__menu {
    overflow-y: auto;
    overflow-x: hidden;
    position: absolute;
    z-index: 99;
    left: 0;
    margin-top: 5px;
    box-sizing: border-box;
    width: auto;
    min-width: 100%;
    min-width: var(--select-menu-min-width);
    max-width: 'auto';
    max-width: var(--select-menu-max-width);
    max-height: 250px;
    max-height: var(--select-menu-max-height);
    border: 1px solid #AFAFAF;
    border: var(--select-menu-border);
    border-radius: 6px;
    border-radius: var(--select-menu-border-radius);
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
    box-shadow: var(--select-menu-box-shadow);
    &-item {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      overflow-x: hidden;
      margin: 0;
      outline: none;
      padding: 0 16px;
      cursor: default;
      border-top: 1px solid #DBDBDB;
      background-color: #ffffff;
      min-height: 45px;
      min-height: var(--select-menu-item-height);
      &:nth-child(1) {
        border-top: none;
      }
      &:focus, &.is-selected {
        background-color: #016699;
        #{ $self }__menu-item-name {
          color: #fff;
        }
      }
      &.is-disabled {
        background-color: #fff;
        #{ $self }__menu-item-name {
          color:#afafaf;
        }
      }
      &-name {
        margin: 0;
        padding: 8px 16px;
        line-height: 1.2;
        color: #016699;
        display: inline-block;
        user-select: none;
        white-space: normal;
        width: auto;
      }
    }
  }
  &__error-message {
    color: #CE3C31;
    font-size:12px;
    margin-left:4px;
  }
  &__no-results {
    background-color: #ffffff;
    padding: 0 1em;
    font-size: 16px;
    color: #4F4F4F;
    width: 100%;
    height: 45px;
    height: var(--select-menu-item-height);
    line-height: 45px;
    line-height: var(--select-menu-item-height);
  }
  &__tag-container {
    border:1px solid #c7c7c7;
    border-radius:4px;
    display:flex;
    flex-wrap:wrap;
    gap:8px;
    min-height:50px;
    padding:8px;
    width:350px;
  }
  &__tag-container:focus {
    border: 2px solid #016699;
  }
}

::v-deep .vcl-select__tag-container > div {
  background-color:#E5F0F6;
  border:1px solid #A7CCE1;
  border-radius:4px; color:#1B3664;
  display:inline-block;
  font-size:12px;
  padding-left:8px;
  padding-top:10px;
  padding-right:0px;
  padding-bottom:10px;
}

::v-deep .vcl-select__tag-container > div.vcl-select__tag > div.tagDeleteDiv {
 display:inline-block;
 padding-left:8px;
 padding-right:8px;
}

.vcl-select-tag__label-row {
  display:flex;
  justify-content: space-between;
  padding-right:8px;
  width:100%;
}

.vcl-select-tag__label {
  color:#4f4f4f;
  display: flex;
  margin: 0 0 0 4px;
  box-sizing: border-box;
  white-space:normal;
  &.is-disabled {
    color:#afafaf;
  }
}

.vcl-select__menu-item-name {
  font-size: 16px;
  font-size: var(--select-font-size);
}
</style>
